import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Eye, EyeOff, KeyRound, Loader2, LockKeyhole, AlertCircle } from "lucide-react";
import AxiosInstance from "../../axios/AxiosInstance";
import "../LoginPage.css";

export default function ResetPassword() {
  const { token } = useParams();
  const [tokenStatus, setTokenStatus] = useState("checking");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let active = true;
    setTokenStatus("checking");
    setIsResetPassword(false);
    setPassword("");
    setConfirmPassword("");
    setError("");
    AxiosInstance.post("auth/check-password-token", { token })
      .then(() => { if (active) setTokenStatus("valid"); })
      .catch((e) => { if (active) setTokenStatus(e.response && [400, 401, 403, 404, 410, 422].includes(e.response.status) ? "invalid" : "unavailable"); });
    return () => { active = false; };
  }, [token]);

  const resetPassword = async (event) => {
    event.preventDefault();
    if (loading || tokenStatus !== "valid") return;
    if (password.length < 8) { setError("Votre mot de passe doit contenir au moins 8 caractères."); return; }
    if (password !== confirmPassword) { setError("Les deux mots de passe ne correspondent pas."); return; }
    setError("");
    setLoading(true);
    try {
      await AxiosInstance.post("auth/reset-password", { token, password });
      setIsResetPassword(true);
      setPassword("");
      setConfirmPassword("");
    } catch (e) {
      setError("La réinitialisation n’a pas abouti. Réessayez ou demandez un nouveau lien.");
    } finally { setLoading(false); }
  };

  return (
    <div className="jm-login">
      <header className="jm-login-header">
        <a href="https://www.jm-interim.com/" className="jm-login-brand" aria-label="J’M Intérim, accueil"><img src="/brand/jm-interim-logo.jpg" alt="J’M Intérim — L’humain au cœur de nos missions" /></a>
        <Link to="/login" className="jm-login-back"><ArrowLeft size={16} aria-hidden="true" /> Connexion</Link>
      </header>
      <main className="jm-login-content">
        <section className="jm-login-access" aria-labelledby="reset-title" aria-busy={loading || tokenStatus === "checking"}>
          <div className="jm-login-card-heading"><span className="jm-login-symbol" aria-hidden="true">{tokenStatus === "checking" ? <Loader2 size={22} className="animate-spin" /> : isResetPassword ? <Check size={22} /> : tokenStatus !== "valid" ? <AlertCircle size={22} /> : <KeyRound size={22} strokeWidth={1.6} />}</span><p className="jm-login-kicker">ESPACE RECRUTEUR</p></div>
          {tokenStatus === "checking" ? (
            <div role="status"><h1 id="reset-title">Vérification du lien<span>.</span></h1><p className="jm-login-description">Nous vérifions votre lien de réinitialisation…</p></div>
          ) : isResetPassword ? (
            <div role="status"><h1 id="reset-title">Votre mot de passe est prêt<span>.</span></h1><p className="jm-login-description">Votre nouveau mot de passe a bien été enregistré. Vous pouvez maintenant vous connecter à votre espace recruteur.</p><Link to="/login" className="jm-login-submit">Me connecter <ArrowRight size={18} aria-hidden="true" /></Link></div>
          ) : tokenStatus !== "valid" ? (
            <div><h1 id="reset-title">{tokenStatus === "invalid" ? "Ce lien n’est plus valide" : "Vérification indisponible"}<span>.</span></h1><p className="jm-login-description">{tokenStatus === "invalid" ? "Votre lien a expiré ou a déjà été utilisé. Demandez un nouveau lien pour réinitialiser votre mot de passe." : "Nous ne pouvons pas vérifier votre lien pour le moment. Actualisez cette page pour réessayer."}</p><Link to="/forgot-password" className="jm-login-submit">Demander un nouveau lien <ArrowRight size={18} aria-hidden="true" /></Link></div>
          ) : (
            <>
              <h1 id="reset-title">Un nouveau mot de passe<span>.</span></h1>
              <p className="jm-login-description">Choisissez un mot de passe d’au moins 8 caractères, puis confirmez-le.</p>
              <form onSubmit={resetPassword} className="jm-login-form">
                <div>
                  <label htmlFor="new-password">Nouveau mot de passe</label>
                  <div className="jm-login-input"><LockKeyhole size={19} aria-hidden="true" /><input id="new-password" name="password" type={visible ? "text" : "password"} autoComplete="new-password" minLength={8} required disabled={loading} value={password} onChange={event => { setPassword(event.target.value); setError(""); }} placeholder="Au moins 8 caractères" aria-describedby={error ? "password-error" : undefined} /><button type="button" onClick={() => setVisible(!visible)} aria-label={visible ? "Masquer les mots de passe" : "Afficher les mots de passe"} aria-pressed={visible}>{visible ? <EyeOff size={19} /> : <Eye size={19} />}</button></div>
                </div>
                <div>
                  <label htmlFor="confirm-password">Confirmer le mot de passe</label>
                  <div className="jm-login-input"><LockKeyhole size={19} aria-hidden="true" /><input id="confirm-password" name="confirmPassword" type={visible ? "text" : "password"} autoComplete="new-password" minLength={8} required disabled={loading} value={confirmPassword} onChange={event => { setConfirmPassword(event.target.value); setError(""); }} placeholder="Saisissez-le à nouveau" aria-describedby={error ? "password-error" : undefined} /></div>
                </div>
                {error && <p id="password-error" role="alert" className="jm-login-error">{error}</p>}
                <button type="submit" disabled={loading} className="jm-login-submit">{loading ? <><span>Enregistrement…</span><Loader2 size={18} className="animate-spin" aria-hidden="true" /></> : <><span>Enregistrer mon mot de passe</span><ArrowRight size={18} aria-hidden="true" /></>}</button>
              </form>
            </>
          )}
        </section>
      </main>
      <footer className="jm-login-footer"><span>© {new Date().getFullYear()} J’M Intérim</span><span>L’humain au cœur de nos missions.</span><a href="https://www.jm-interim.com/contact">Contacter l’équipe</a></footer>
    </div>
  );
}
